import axios from "axios";
import { jwtDecode } from "jwt-decode";

const URL =
  process.env.REACT_APP_API_BASE_PROD_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:3002"; // Fallback to localhost if not set
// User API functions

export async function createInvoice(invoice) {
  try {
    const response = await axios.post(`${URL}/invoices`, invoice);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating invoice:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Create an invoice from a onlineBooking:
export async function createOnlineInvoice({
  selectedDate,
  selectedPractice,
  selectedPractitioner,
  selectedService,
  patientId,
  transactionRef,
  appointmentId,
}) {
  try {
    const response = await axios.post(`${URL}/invoices/onlineCreate`, {
      selectedDate,
      selectedPractice,
      selectedPractitioner,
      selectedService,
      patientId,
      transactionRef,
      appointmentId,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating invoice:",
      error.response?.data || error.message
    );
    throw error;
  }
}



export async function updateInvoice(id, invoice) {
  try {
    const response = await axios.put(`${URL}/invoices/${id}`, invoice);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error updating invoice:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function deleteInvoice(id) {
  try {
    const response = await axios.delete(`${URL}/invoices/${id}`);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error deleting invoice:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function getStatementByAccount(
  accountId,
  startDate,
  endDate,
  practiceIds
) {
  try {
    const response = await axios.post(`${URL}/invoices/statementaccount`, {
      accountId,
      startDate,
      endDate,
      practiceIds,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching invoices:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Get all the data for the statement by account to export statement to pdf:
export async function getStatementToExport(
  accountId,
  startDate,
  endDate,
  practiceNumbers
) {
  try {
    const response = await axios.post(`${URL}/account/statement/details`, {
      accountId,
      startDate,
      endDate,
      practiceNumbers,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching account statement:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Generate statement PDF on the server and return as a Blob
export async function getStatementPDF(accountId, startDate, endDate, practiceNumbers) {
  const token = sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const response = await axios.post(
    `${URL}/account/statement/pdf`,
    { accountId, startDate, endDate, practiceNumbers },
    { headers, responseType: "blob" }
  );
  return response.data; // Blob
}

export async function getReceiptsReportToExport(
  startDate,
  endDate,
  practiceNumbers,
  accountId
) {
  try {
    const response = await axios.post(`${URL}/receipts/report`, {
      startDate,
      endDate,
      practiceNumbers,
      accountId,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching receipts report:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function getAllocationReport({
  practiceNumbers,
  startDate,
  endDate,
  methods,
  practitioners,
  sortKey,
  sortDir,
  page,
  limit,
  accountId,
} = {}) {
  try {
    const response = await axios.post(`${URL}/receipts/report`, {
      practiceNumbers,
      startDate,
      endDate,
      methods,
      practitioners,
      sortKey,
      sortDir,
      page,
      limit,
      accountId,
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching allocation report:", error.response?.data || error.message);
    throw error;
  }
}

// 📦 Allocate receipt to selected invoices
export async function allocateReceipt({
  accountId,
  receiptDate,
  receiptTotalAmount,
  unallocated,
  receiptMethod,
  receiptPaymentMethod, // Keep for backwards compatibility
  receiptInvoices,
  receiptPracticeNumber,
  receiptReference,
  unallocatedDrawn = 0,
}) {
  try {
    const response = await axios.post(`${URL}/invoices/receipts/allocate`, {
      accountId,
      receiptDate,
      receiptTotalAmount,
      unallocated,
      receiptMethod: receiptMethod || receiptPaymentMethod, // Use receiptMethod first, fallback to old field
      receiptInvoices,
      receiptPracticeNumber,
      receiptReference,
      unallocatedDrawn,
    });

    return response.data;
  } catch (error) {
    console.error(
      "❌ Error allocating receipt:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function createCreditNote(creditNoteData) {
  try {
    const response = await axios.post(
      `${URL}/invoices/credit-notes/create`,
      {
        creditNoteDate:             creditNoteData.creditNoteDate,
        creditNoteTotalAmount:      creditNoteData.creditNoteTotalAmount,
        creditNotePaymentMethod:    creditNoteData.creditNotePaymentMethod,
        creditNoteReference:        creditNoteData.creditNoteReference,
        creditNotePracticeNumber:   creditNoteData.creditNotePracticeNumber,
        creditNotePracticeVAT:      creditNoteData.creditNotePracticeVAT,
        accountId:                  creditNoteData.accountId,
        unAllocatedBalance:         creditNoteData.unAllocatedBalance,
        creditNoteInvoiceLines:     creditNoteData.creditNoteInvoiceLines,
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating credit note:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function createWriteOff(writeOffData) {
  try {
    const response = await axios.post(`${URL}/invoices/write-offs/create`, {
      accountId: writeOffData.accountId,
      writeOffDate: writeOffData.writeOffDate,
      writeOffNote: writeOffData.writeOffNote,
      writeOffPracticeNumber: writeOffData.writeOffPracticeNumber,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating write-off:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// get all invoices by account that are not "Paid" or "Cancelled":
export async function getInvoicesByAccount(accountId) {
  try {
    const response = await axios.post(`${URL}/invoices/account`, {
      accountId,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching invoices:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Get all invoices by account that are not "Draft" or "Cancelled" for a specific time period (full invoice object):

export async function getFullInvoicesByAccount(accountId, startDate, endDate) {
  try {
    const response = await axios.post(`${URL}/invoices/fullbyaccount`, {
      accountId,
      startDate,
      endDate,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching full invoices:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function getAgeAnalysis({ practiceNumbers, startDate, endDate, statuses = [], aging = [], practices = [], sortKey = null, sortDir = "asc", page = 1, limit = 20 }) {
  const response = await axios.post(`${URL}/invoices/ageAnalysis`, {
    practiceNumbers, startDate, endDate, statuses, aging, practices, sortKey, sortDir, page, limit
  }, { headers: { Authorization: `Bearer ${sessionStorage.getItem("user")}` } });
  return response.data; // { data, total, totals }
}

export async function getAgeAnalysisExport({ practiceNumbers, startDate, endDate, statuses = [], aging = [], practices = [], sortKey = null, sortDir = "asc" }) {
  const response = await axios.post(`${URL}/invoices/ageAnalysis/export`, {
    practiceNumbers, startDate, endDate, statuses, aging, practices, sortKey, sortDir
  }, { headers: { Authorization: `Bearer ${sessionStorage.getItem("user")}` } });
  return response.data; // Invoice[]
}

// Get all invoices by an array of practiceNumbers that are not "Paid" or "Cancelled" and age analysis:
export async function getAgedInvoicesByPracticeNumbers(practiceNumbers) {
  try {
    const response = await axios.post(`${URL}/invoices/byPracticeNumbers`, {
      practiceNumbers,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching aged invoices:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Get the sales report by an array of practiceNumber between the start and end dates. PENDING and DRAFT is excluded:

export async function getSalesReportByPracticeNumbers({
  practiceNumbers,
  startDate,
  endDate,
  statuses,
  sortKey,
  sortDir,
  page,
  limit,
}) {
  try {
    const response = await axios.post(`${URL}/invoices/salesReport`, {
      practiceNumbers,
      startDate,
      endDate,
      statuses,
      sortKey,
      sortDir,
      page,
      limit,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching full invoices:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Get all invoices by an array of practiceNumbers that are not "Paid" or "Cancelled" and age analysis:
export async function getInvoicesByPracticeNumbers(practiceNumbers) {
  try {
    const response = await axios.post(
      `${URL}/invoices/byPracticeNumbers/full`,
      {
        practiceNumbers,
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching aged invoices:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Get all paymentLinks and payments by an array of practiceNumbers that are not "Paid" or "Cancelled" with age analysis
export async function getAllPaymentLinks(practiceIds) {
  try {
    const response = await axios.post(`${URL}/paymentlinks/all`, {
      practiceIds,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching payment links:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Get aged invoices for a specific accountId, split by Patient and Medical Aid
export async function getAgedBalancesByAccount(accountId) {
  try {
    const response = await axios.post(`${URL}/invoices/agedByAccount`, {
      accountId,
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error fetching aged balances by accountId:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function updateAccountEdi(accountId, ediDisabled, ediEndDate) {
  const token = sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const { data } = await axios.post(`${URL}/account/edi`, {
    accountId,
    ediDisabled,
    ediEndDate,
  }, { headers });
  return data;
}

export async function updateAccountShowOnStatement(accountId, showOnStatement) {
  const token = sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const { data } = await axios.post(`${URL}/account/statement/show-on-statement`, {
    accountId,
    showOnStatement,
  }, { headers });
  return data;
}

export async function searchICD10(searchQuery) {
  try {
    const response = await axios.get(`${URL}/icd10-codes`, {
      params: { searchQuery },
    });

    return response.data; // Return the filtered results
  } catch (error) {
    console.error("Error searching accounts:", error.message);
    return [];
  }
}

//Get all the places of service for the invoice:
export async function getPlacesOfService() {
  try {
  const response = await axios.get(`${URL}/placeservice/all`);
return response.data;
  } catch (error) {
    console.error("Error fetching places of service:", error.message);
    return [];
  }
}

// Get an invoice by ID:
export async function getInvoiceById(invoiceId) {
  try {
    const response = await axios.get(`${URL}/invoices/${invoiceId}`);

    if (response.status === 200) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching invoice by ID:", error);
    return null;
  }
}

export async function sendStatementByEmail(formData) {
  const token = sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const { data } = await axios.post(`${URL}/statements/sendStatementByEmail`, formData, {
    headers, // ✅ DON'T set Content-Type manually for FormData
  });

  return data;
}


export async function sendInvoiceByEmail({
  email,
  emails,                // ✅ NEW: array of emails
  type = "invoice",
  accountId,
  pdfBlob,
  emailData,
}) {
  const form = new FormData();

  const list = Array.isArray(emails) && emails.length
    ? emails
    : (email ? [email] : []);

  // Send as CSV in "email" (your route can split commas)
  form.append("email", list.join(","));

  form.append("type", type);
  if (accountId) form.append("accountId", accountId);

  // pdfBlob MUST be a Blob/File here
  form.append("pdf", pdfBlob, `${type}.pdf`);

  if (emailData) form.append("emailData", JSON.stringify(emailData));

  const token =
    sessionStorage.getItem("userToken") || sessionStorage.getItem("user");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const { data } = await axios.post(`${URL}/invoices/sendInvoiceByEmail`, form, {
    headers,
  });

  return data;
}



// Create a new claim
export async function createClaim(claim) {
  try {
    const response = await axios.post(`${URL}/claims`, claim);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating claim:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Update an existing claim
export async function updateClaim(id, claim) {
  try {
    const response = await axios.put(`${URL}/claims/${id}`, claim);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error updating claim:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Get all claims for specific practice IDs
export async function getAllClaims(practiceIds) {
  try {
    const response = await axios.post(`${URL}/claims/all`, {
      practiceIds, // expects an array of practice ID strings
    });

    if (response.status === 200) {
      return response.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error retrieving claims:", error);
    return [];
  }
}

// Get a claim by ID
export async function getClaimByInvoiceId(invoiceId) {
  try {
    const response = await axios.post(`${URL}/claims/byinvoiceid`, {
      invoiceId, // Send claimId in the request body
    });

    if (response.status === 200) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error retrieving account:", error);
    return null;
  }
}

// Get all ERAS for specific practice IDs (paginated, sorted, filtered by the
// backend). Returns { data, total, page, limit } where each ERA also carries
// an `accountSummary` field with allocated/unallocated breakdowns.
//
// Legacy callers that just passed a `practiceIds` array still work — the
// signature accepts either an array or an options object.
export async function getAllEras(arg = {}) {
  try {
    const opts = Array.isArray(arg) ? { practiceIds: arg } : arg;
    const body = {
      practiceIds: opts.practiceIds || [],
      startDate: opts.startDate || null,
      endDate: opts.endDate || null,
      medicalAidNames: Array.isArray(opts.medicalAidNames) ? opts.medicalAidNames : [],
      sortKey: opts.sortKey || "header.ERADate",
      sortDir: opts.sortDir || "desc",
      page: opts.page || 1,
      limit: opts.limit || 20,
    };
    const response = await axios.post(`${URL}/eras/all`, body);
    if (response.status === 200) {
      // Back-compat: if the caller just wants the raw array, the new shape
      // wraps it in { data, total }. Most callers should prefer the wrapper.
      return response.data;
    }
    return { data: [], total: 0, page: 1, limit: body.limit };
  } catch (error) {
    console.error("Error retrieving ERAs:", error);
    return { data: [], total: 0, page: 1, limit: 20 };
  }
}

// Distinct medical-aid names available across the user's practices' ERAs —
// used to populate the filter dropdown without fetching the full ERA list.
export async function getEraMedicalAidNames(practiceIds) {
  try {
    const response = await axios.post(`${URL}/eras/medical-aid-names`, { practiceIds });
    if (response.status === 200) {
      return response.data?.medicalAidNames || [];
    }
    return [];
  } catch (error) {
    console.error("Error retrieving ERA medical aid names:", error);
    return [];
  }
}

// Get an ERA by ID

export async function getERAById(eraId) {
  try {
    const response = await axios.get(`${URL}/eras/${eraId}`);

    if (response.status === 200) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error retrieving ERA:", error);
    return null;
  }
}



// Create new banking / payment details
export async function createBankingDetails(bankingData) {
  try {
    const response = await axios.post(`${URL}/banking/create`, bankingData);
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating banking details:",
      error.response?.data || error.message
    );
    throw error;
  }
}

export async function getBankingDetailsByPracticeNumbers(practiceNumbers) {
  const response = await axios.post(`${URL}/banking/byPractices`, {
    practiceNumbers,
  });
  return response.data;
}

// Get the Ozow online payment status by an array of practiceNumbers:
export async function getOzowOnlinePaymentStatus(practiceNumbers) {
  try {
    const response = await axios.post(
      `${URL}/banking/ozow-status/byPractices`,
      {
        practiceNumbers,
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      console.error(
        "Unexpected response from Ozow status endpoint:",
        response.status
      );
      return [];
    }
  } catch (error) {
    console.error("Error fetching Ozow online payment status:", error);
    return [];
  }
}

// Decrypt an encrypted field
export async function decryptField(value) {
  const response = await axios.post(`${URL}/banking/decrypt`, { value });
  return response.data.decrypted;
}

// Edit banking details by ID
export async function updateBankingDetails(id, updatedData) {
  try {
    const response = await axios.put(
      `${URL}/banking/update/${id}`,
      updatedData
    );
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error updating banking details:",
      error.response?.data || error.message
    );
    throw error;
  }
}

// Generate a payment link for Ozow to use on the frontend:
export async function generateOzowLink({
  practiceNumber,
  totalSelectedAmount,
  accountLastName,
  transactionRef,
}) {
  try {
    const response = await axios.post(`${URL}/banking/generateOzowLink`, {
      practiceNumber,
      totalSelectedAmount,
      accountLastName,
      transactionRef,
    });

    // Check for successful response
    if (response.status === 200 && response.data) {
      // Return the form data and action URL for form submission
      if (response.data.formData && response.data.actionUrl) {
        return {
          formData: response.data.formData,
          actionUrl: response.data.actionUrl
        };
      } else {
        console.error("No valid form data found in response:", response.data);
        return null;
      }
    } else {
      console.error(
        "Unexpected response while generating Ozow form data:",
        response.status,
        response.data
      );
      return null;
    }
  } catch (error) {
    // More detailed error logging
    if (error.response) {
      console.error("Server error generating Ozow form data:", {
        status: error.response.status,
        data: error.response.data,
        message: error.message
      });
    } else if (error.request) {
      console.error("Network error generating Ozow form data:", error.message);
    } else {
      console.error("Error generating Ozow form data:", error.message);
    }
    return null;
  }
}

// Send Ozow link
export async function sendOzowLink({
  practiceNumber,
  accountFirstName,
  accountLastName,
  email,
  totalSelectedAmount,
  transactionRef,
}) {
  try {
    const response = await axios.post(`${URL}/banking/sendOzowLink`, {
      practiceNumber,
      accountFirstName,
      accountLastName,
      email,
      totalSelectedAmount,
      transactionRef,
    });

    if (response.status === 200) {
      return response.data;
    } else {
      console.error(
        "Unexpected status when sending Ozow link:",
        response.status,
        response.data
      );
      return null;
    }
  } catch (error) {
    console.error("Error sending Ozow link:", error);
    return null;
  }
}

export async function createPaymentLink(payload) {
  try {
    const response = await axios.post(`${URL}/paymentLinks`, payload, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "❌ Error creating payment link:",
      error.response?.data || error.message
    );
    throw error;
  }
}


// Get modifiers by discipline code
export async function getModifierByDisciplineCode(disciplineCode) {
  try {
    const response = await axios.post(`${URL}/modifier/bydisciplinecode`, {
      disciplineCode, // Send disciplineCode in the request body
    });

    if (response.status === 200) {
      return response.data;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error retrieving modifier:", error);
    return null;
  }
}

export async function getReceiptByNumber(receiptNumber) {
  try {
    const response = await axios.post(`${URL}/invoices/receiptbynumber`, {
      receiptNumber,
    });
    return response.status === 200 ? response.data : null;
  } catch (err) {
    console.error("Error retrieving receipt:", err);
    return null;
  }
}

export async function getCreditNoteByNumber(cnNumber) {
  try {
    const response = await axios.post(`${URL}/invoices/cnbynumber`, {
      cnNumber,
    });
    return response.status === 200 ? response.data : null;
  } catch (err) {
    console.error("Error retrieving receipt:", err);
    return null;
  }
}

// Generate Bulk PDF:
export async function generateBulkInvoicePDF(
  accountId,
  startDate,
  endDate,
  options
) {
  const token = sessionStorage.getItem("user");
  const decoded = jwtDecode(token);
  const userId = decoded._id;
  const response = await axios.post(
    `${URL}/generate-pdf-bulk`,
    {
      accountId,
      startDate,
      endDate,
      options, // ✅ sends all export booleans to backend
    },
    {
      headers: {
        "x-user-id": userId, // 👈 Custom header for socket identification
      },
    }
  );
  return response.data;
}

export async function getBulkInvoicePDF(downloadId) {
  try {
    const response = await axios.get(`${URL}/download/${downloadId}`, {
      responseType: "blob", // Required to handle binary PDF data
    });

    // Create a Blob and initiate download
    const blob = new Blob([response.data], { type: "application/pdf" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `BulkInvoices-${downloadId}.pdf`; // This should match the backend's filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl); // Clean up the object URL
  } catch (err) {
    console.error("Error downloading bulk invoice PDF:", err);
  }
}

// Create (new) finComms doc
export async function createReminder(payload) {
  try {
    const { data } = await axios.post(`${URL}/finComms`, payload);
    return data;
  } catch (error) {
    console.error("❌ Error creating finComms:", error.response?.data || error.message);
    throw error;
  }
}

// Update existing finComms doc by id
export async function updateReminder(id, payload) {
  try {
    const { data } = await axios.put(`${URL}/finComms/${id}`, payload);
    return data;
  } catch (error) {
    console.error("❌ Error updating finComms:", error.response?.data || error.message);
    throw error;
  }
}

// (Optional) Fetch the most recent by practice
export async function getFinComms(practiceId) {
  try {
    const qs = practiceId ? `?practiceId=${practiceId}` : "";
    const { data } = await axios.get(`${URL}/finComms${qs}`);
    return data; // array
  } catch (error) {
    console.error("❌ Error fetching finComms:", error.response?.data || error.message);
    throw error;
  }
}

// (Optional) Fetch single by id
export async function getFinCommsById(id) {
  try {
    const { data } = await axios.get(`${URL}/finComms/${id}`);
    return data;
  } catch (error) {
    console.error("❌ Error fetching finComms by id:", error.response?.data || error.message);
    throw error;
  }
}

export async function getYocoAuthorizeUrl(practiceNumber, returnTo) {
  const { data } = await axios.get(`${URL}/yoco/auth-url`, {
    params: { practiceNumber, returnTo },
  });
  return data.url; // string
}

export async function getYocoStatus(practiceNumber) {
  const { data } = await axios.get(`${URL}/yoco/status`, {
    params: { practiceNumber },
  });
  return data; // { connected: boolean, tokens?: { obtained_at, expires_in } }
}

export async function createYocoWebposDevice(practiceNumber, name) {
  const { data } = await axios.post(`${URL}/yoco/webpos/devices`, { practiceNumber, name });
  return data.device; // { id, name, created_at }
}

// List Web POS devices (from your DB)
export async function listYocoWebposDevices(practiceNumber) {
  const { data } = await axios.get(`${URL}/yoco/webpos/devices`, { params: { practiceNumber } });
  return data.devices || [];
}

export async function createWebposPayment(deviceId, payload) {
  const { data } = await axios.post(`${URL}/yoco/webpos/${deviceId}/payments`, payload);
  return data; // { paymentId, redirectUrl, status }
}

export async function fetchWebposPayment(deviceId, paymentId, practiceNumber) {
  const { data } = await axios.get(`${URL}/yoco/webpos/${deviceId}/payments/${paymentId}`, {
    params: { practiceNumber },
  });
  return data; // { status, details }
}

// Unallocated ERA claims for a single account. Backend matches by
// accountReference (era:Reference) with a fallback on accountMedicalAidNumber.
export async function getUnallocatedERAsForAccount(accountId) {
  const token = sessionStorage.getItem("user");
  const { data } = await axios.get(
    `${URL}/eras/unallocated/by-account/${accountId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data?.data || [];
}

// Mark a specific unallocated ERA claim as allocated (so it disappears from
// the per-account list).
export async function markERAClaimAllocated({ eraLogId, schemeClaimId, treatmentDate }) {
  const token = sessionStorage.getItem("user");
  const { data } = await axios.post(
    `${URL}/eras/unallocated/allocate`,
    { eraLogId, schemeClaimId, treatmentDate },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
}

// Dismiss an unallocated ERA claim. Same removal effect as allocate but
// records the user's intent to ignore the entry rather than apply it.
export async function markERAClaimDismissed({ eraLogId, schemeClaimId, treatmentDate }) {
  const token = sessionStorage.getItem("user");
  const { data } = await axios.post(
    `${URL}/eras/unallocated/dismiss`,
    { eraLogId, schemeClaimId, treatmentDate },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
}
